import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getGameBadgeClass = (game) => {
  switch (game) {
    case 'yugioh': return 'badge-yugioh';
    case 'magic': return 'badge-magic';
    case 'pokemon': return 'badge-pokemon';
    default: return 'bg-gray-600';
  }
};

const getGameLabel = (game) => {
  switch (game) {
    case 'yugioh': return 'Yu-Gi-Oh!';
    case 'magic': return 'Magic';
    case 'pokemon': return 'Pokémon';
    default: return game;
  }
};

const getCategoryBadgeClass = (category) => {
  switch (category) {
    case 'deck': return 'badge-deck';
    case 'booster': return 'badge-booster';
    case 'single': return 'badge-single';
    default: return 'bg-gray-600';
  }
};

const getCategoryLabel = (category) => {
  switch (category) {
    case 'deck': return 'Deck';
    case 'booster': return 'Booster';
    case 'single': return 'Carte';
    default: return category;
  }
};

export const ProductCard = ({ product, isFavorite = false, onFavoriteToggle }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();

  const handleCardClick = () => {
    navigate(`/product/${product.product_id}`);
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      toast.error('Connectez-vous pour ajouter au panier');
      return;
    }
    
    try {
      await addToCart(product.product_id, 1);
      toast.success('Ajouté au panier');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'ajout au panier');
    }
  };

  const handleFavoriteToggle = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${product.product_id}`, { withCredentials: true });
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/${product.product_id}`, {}, { withCredentials: true });
        toast.success('Ajouté aux favoris');
      }
      if (onFavoriteToggle) onFavoriteToggle();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="group cursor-pointer"
      data-testid={`product-card-${product.product_id}`}
    >
      <div className={`bg-[#121212] border border-white/10 rounded-xl overflow-hidden transition-all duration-300 ${product.stock > 0 ? 'hover:-translate-y-1 hover:border-white/20' : 'opacity-50 grayscale'}`}>
        {/* Image Container */}
        <div className="relative aspect-square bg-black/40 overflow-hidden">
          <img
            src={product.image_url}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-300 ${product.stock > 0 ? 'group-hover:scale-105' : ''}`}
            loading="lazy"
          />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
            <span className={`px-2 py-1 rounded text-xs font-semibold ${getGameBadgeClass(product.game)}`}>
              {getGameLabel(product.game)}
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryBadgeClass(product.category)}`}>
              {getCategoryLabel(product.category)}
            </span>
          </div>

          {/* Favorite Button */}
          <button
            onClick={handleFavoriteToggle}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all z-20 ${
              isFavorite 
                ? 'bg-fuchsia-600 text-white' 
                : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-white'
            }`}
            data-testid={`favorite-btn-${product.product_id}`}
          >
            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>

          {/* Promo Badge */}
          {product.discount_percent && (
            <div className="absolute top-12 left-3 z-10">
              <span className="px-2 py-1 bg-red-600 rounded text-xs text-white font-bold">
                -{product.discount_percent}%
              </span>
            </div>
          )}

          {/* Preorder Badge */}
          {product.is_preorder && (
            <div className={`absolute ${product.discount_percent ? 'top-[4.5rem]' : 'top-12'} left-3 z-10`}>
              <span className="px-2 py-1 bg-cyan-600 rounded text-xs text-white font-bold">
                Précommande
              </span>
            </div>
          )}

          {/* Rarity Badge */}
          {product.rarity && (
            <div className="absolute bottom-3 left-3 z-10">
              <span className="px-2 py-1 bg-black/70 backdrop-blur-sm rounded text-xs text-fuchsia-400 font-medium uppercase tracking-wider">
                {product.rarity}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="text-white font-semibold text-sm mb-2 line-clamp-2 min-h-[40px]">
            {product.name}
          </h3>
          
          <div className="flex items-center justify-between">
            <div>
              {product.original_price ? (
                <>
                  <span className="text-sm text-gray-500 line-through mr-2">{product.original_price.toFixed(2)} €</span>
                  <span className="text-xl font-bold text-red-400">{product.price.toFixed(2)} €</span>
                </>
              ) : (
                <span className="text-xl font-bold text-fuchsia-400">{product.price.toFixed(2)} €</span>
              )}
            </div>
            
            <Button
              size="sm"
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white relative z-20 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`add-to-cart-${product.product_id}`}
            >
              <ShoppingCart className="w-4 h-4" />
            </Button>
          </div>

          {/* Stock / Preorder info */}
          {product.is_preorder ? (
            <p className="text-xs mt-2 text-cyan-400">
              Précommande{product.release_date ? ` — Sortie: ${product.release_date}` : ''}
            </p>
          ) : (
            <p className={`text-xs mt-2 ${product.stock > 5 ? 'text-green-400' : product.stock > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
              {product.stock > 5 ? 'En stock' : product.stock > 0 ? `Plus que ${product.stock}` : 'Rupture'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
